import {
  ApiErrorState,
  Badge,
  Button,
  FieldArray,
  FieldArrayItem,
  FieldHint,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormLayout,
  FormMessage,
  FormSection,
  Input,
  MarkdownInput,
  MobileNavDrillProvider,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  SwitchCard,
  toast,
  useMobileNavDrillTop,
} from '@avila-roffe/components-web';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  BookOpen,
  MessageSquare,
  Play,
  RotateCcw,
  SlidersHorizontal,
  Wrench,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { type Control, useFieldArray, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { ResourceAttachmentPanel } from '../../attachments/ResourceAttachmentPanel';
import { usePageHeader } from '../../page-header/PageHeaderContext';
import { useSkills } from '../skills/hooks/use-skills';
import { useTools } from '../tools/hooks/use-tools';
import {
  CAPABILITIES,
  CapabilityBadge,
  CapabilityLegendPopover,
  CapabilityToggle,
  ModelSelectPopover,
  modelCapabilityKeys,
} from './components/model-picker';
import { ToolSkillPickerDialog } from './components/ToolSkillPickerDialog';
import { useAgentDetail, useAgentRelease, useUpdateRelease } from './hooks/use-agent-detail';
import { useModelRegistry } from './hooks/use-agents';
import type { Agent, AgentReleaseSnapshot } from './types';

// ─── Behavior form schema ─────────────────────────────────────────────────────

const behaviorSchema = z.object({
  modelAlias: z.string().min(1, 'Required'),
  approvalMode: z.enum(['NONE', 'REQUIRED', 'SUGGESTED']),
  maxToolCalls: z.coerce.number().int().min(1),
  maxSteps: z.coerce.number().int().min(1),
  initialSalutation: z.boolean(),
  salutationExamples: z.array(z.object({ value: z.string() })),
  requiredCapabilities: z.array(z.string()),
});

type BehaviorFormValues = z.infer<typeof behaviorSchema>;

function buildBehaviorDefaults(snap: AgentReleaseSnapshot): BehaviorFormValues {
  const g = snap.guardrails ?? {};
  return {
    modelAlias: snap.modelAlias ?? '',
    approvalMode:
      g.approvalMode === 'NONE' || g.approvalMode === 'REQUIRED' || g.approvalMode === 'SUGGESTED'
        ? (g.approvalMode as BehaviorFormValues['approvalMode'])
        : 'SUGGESTED',
    maxToolCalls: typeof g.maxToolCalls === 'number' ? g.maxToolCalls : 20,
    maxSteps: typeof g.maxSteps === 'number' ? g.maxSteps : 50,
    initialSalutation: snap.initialSalutation ?? false,
    salutationExamples: (snap.salutationExamples ?? []).map((v) => ({ value: v })),
    requiredCapabilities: Array.isArray(g.requiredCapabilities)
      ? (g.requiredCapabilities as string[])
      : [],
  };
}

function buildAgentDefaults(agent: Agent): BehaviorFormValues {
  return {
    modelAlias: agent.modelAlias,
    approvalMode: agent.guardrails.approvalMode,
    maxToolCalls: agent.guardrails.maxToolCalls,
    maxSteps: agent.guardrails.maxSteps,
    initialSalutation: agent.interface.initialSalutation ?? false,
    salutationExamples: (agent.interface.salutationExamples ?? []).map((v) => ({ value: v })),
    requiredCapabilities: agent.guardrails.requiredCapabilities ?? [],
  };
}

// ─── Salutation examples array editor ────────────────────────────────────────

function SalutationExamplesField({
  control,
  disabled,
}: {
  control: Control<BehaviorFormValues>;
  disabled?: boolean;
}) {
  const { fields, append, remove } = useFieldArray({ control, name: 'salutationExamples' });
  return (
    <FieldArray
      label="Salutation examples"
      description="The agent picks one of these at random to open the conversation."
      addLabel="Add example"
      onAdd={disabled ? undefined : () => append({ value: '' })}
      itemCount={fields.length}
      disabled={disabled}
    >
      {fields.map((field, index) => (
        <FieldArrayItem
          key={field.id}
          onRemove={disabled ? undefined : () => remove(index)}
          disabled={disabled}
        >
          <FormField
            control={control}
            name={`salutationExamples.${index}.value`}
            render={({ field: f }) => (
              <FormItem>
                <FormControl>
                  <Input {...f} placeholder={`Example ${index + 1}`} disabled={disabled} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </FieldArrayItem>
      ))}
    </FieldArray>
  );
}

// ─── Inner page ───────────────────────────────────────────────────────────────

function ReleaseDetailPageInner({
  tenantSlug = '',
  backPath,
}: {
  tenantSlug?: string;
  backPath?: string;
}) {
  const { id: agentId, releaseId } = useParams<{ id: string; releaseId: string }>();

  const { data: agent } = useAgentDetail(agentId);
  const { data: release, isLoading, error } = useAgentRelease(agentId, releaseId);
  const updateRelease = useUpdateRelease(agentId ?? '', releaseId ?? '');
  const { data: allSkills = [], isLoading: skillsLoading } = useSkills('_all');
  const { data: allTools = [], isLoading: toolsLoading } = useTools();
  const { data: registry = [] } = useModelRegistry();

  const [toolPickerOpen, setToolPickerOpen] = useState(false);
  const [skillPickerOpen, setSkillPickerOpen] = useState(false);

  const isDraft = release?.status === 'DRAFT';
  const snap = release?.snapshot ?? ({} as AgentReleaseSnapshot);

  // Prompt state
  const [promptDraft, setPromptDraft] = useState('');
  const [promptDirty, setPromptDirty] = useState(false);
  const [syncedReleaseId, setSyncedReleaseId] = useState<string | undefined>();
  if (release?.id !== syncedReleaseId) {
    setSyncedReleaseId(release?.id);
    if (release) {
      setPromptDraft(snap.systemPrompt ?? '');
      setPromptDirty(false);
    }
  }

  function handlePromptChange(v: string) {
    setPromptDraft(v);
    setPromptDirty(v !== (snap.systemPrompt ?? ''));
  }

  // Behavior form
  const behaviorForm = useForm<BehaviorFormValues>({
    resolver: isDraft ? zodResolver(behaviorSchema) : undefined,
    defaultValues: buildBehaviorDefaults(snap),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset when release changes
  useEffect(() => {
    if (snap) behaviorForm.reset(buildBehaviorDefaults(snap));
  }, [release?.id]);

  const watchRequiredCaps = behaviorForm.watch('requiredCapabilities');

  const filteredModels = useMemo(() => {
    const requiredModelCaps = modelCapabilityKeys(watchRequiredCaps);
    return registry.filter((m) => m.isActive && requiredModelCaps.every((cap) => m[cap] === true));
  }, [registry, watchRequiredCaps]);

  const filteredProviders = useMemo(
    () =>
      [
        ...new Set(filteredModels.map((m) => m.provider).filter((p): p is string => Boolean(p))),
      ].sort(),
    [filteredModels],
  );

  const skillCatalog = useMemo(
    () =>
      allSkills.map((s) => ({
        value: s.name,
        label: s.interface.displayName || s.name,
        meta: s.appSlug,
        href: `/skills/${s.appSlug}/${s.name}`,
        scope: s.scope,
        description: s.interface.shortDescription,
        status: s.status,
      })),
    [allSkills],
  );

  const toolCatalog = useMemo(
    () =>
      allTools.map((t) => ({
        value: t.name,
        label: (t.interface as { displayName?: string }).displayName || t.name,
        meta: t.appSlug,
        href: `/tools/${t.id}`,
        scope: t.scope,
        description: t.description,
        status: t.status,
      })),
    [allTools],
  );

  const groupedTools = useMemo(() => {
    const groups = new Map<string, { value: string; label: string }[]>();
    for (const ref of snap.toolRefs ?? []) {
      const item = toolCatalog.find((t) => t.value === ref) ?? { value: ref, label: ref };
      const prefix = ref.includes('.') ? ref.split('.')[0] : 'other';
      if (!groups.has(prefix)) groups.set(prefix, []);
      groups.get(prefix)?.push(item);
    }
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [snap.toolRefs, toolCatalog]);

  const groupedSkills = useMemo(() => {
    const groups = new Map<string, { value: string; label: string; meta?: string }[]>();
    for (const ref of snap.skillRefs ?? []) {
      const item = skillCatalog.find((s) => s.value === ref) ?? {
        value: ref,
        label: ref,
        meta: undefined,
      };
      const group = item.meta ?? 'global';
      if (!groups.has(group)) groups.set(group, []);
      groups.get(group)?.push(item);
    }
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [snap.skillRefs, skillCatalog]);

  const agentLabel = agent?.interface?.displayName || agent?.name || agentId || '';

  const drillTop = useMobileNavDrillTop();
  const navigate = useNavigate();

  usePageHeader({
    title: release?.name ?? 'Release',
    titleKey: release?.name ?? 'Release',
    subtitle: release ? `${agentLabel} · ${release.status}` : agentLabel,
    backPath: backPath ?? (agentId ? `/agents/${agentId}?tab=releases` : '/agents'),
    mobileOnBack: drillTop?.onBack,
    action:
      agentId && releaseId ? (
        <Button
          appearance="ghost"
          size="icon"
          className="size-8 text-primary-foreground hover:bg-primary-foreground/20"
          aria-label="Test in playground"
          onClick={() => {
            const params = new URLSearchParams({
              agentId: agentId ?? '',
              releaseId: releaseId ?? '',
            });
            if (snap.modelAlias) params.set('model', snap.modelAlias);
            navigate(`/playground?${params.toString()}`);
          }}
        >
          <Play className="size-4" />
        </Button>
      ) : undefined,
    actionKey: releaseId,
  });

  async function saveAll(data: BehaviorFormValues) {
    console.log('[ReleaseDetailPage] save payload', data);
    try {
      await updateRelease.mutateAsync({
        systemPrompt: promptDraft,
        modelAlias: data.modelAlias,
        guardrails: {
          ...(snap.guardrails ?? {}),
          approvalMode: data.approvalMode,
          maxToolCalls: data.maxToolCalls,
          maxSteps: data.maxSteps,
          requiredCapabilities: data.requiredCapabilities,
        },
        initialSalutation: data.initialSalutation,
        salutationExamples: data.salutationExamples.map((e) => e.value).filter(Boolean),
      });
      behaviorForm.reset(data);
      setPromptDirty(false);
      toast.success('Release saved');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to save');
    }
  }

  async function saveSkillRefs(newRefs: string[]) {
    await updateRelease.mutateAsync({ skillRefs: newRefs });
    toast.success('Skill bindings saved');
  }

  async function saveToolRefs(newRefs: string[]) {
    await updateRelease.mutateAsync({ toolRefs: newRefs });
    toast.success('Tool bindings saved');
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholder
            <Skeleton key={i} className="h-16" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !release) {
    return <ApiErrorState message={error instanceof Error ? error.message : 'Release not found'} />;
  }

  const configIsDirty = behaviorForm.formState.isDirty || promptDirty;

  return (
    <div className="flex flex-col md:flex-1 md:min-h-0 md:overflow-hidden">
      <div className="p-4 md:flex-1 md:min-h-0 md:overflow-y-auto md:pt-4 md:px-6 md:pb-6">
        {isDraft && agent && (
          <div className="flex items-center justify-between rounded-lg border border-border bg-muted/50 px-3 py-2.5 text-xs text-muted-foreground mb-4">
            <span>Sync all configure settings from the source agent.</span>
            <Button
              type="button"
              size="sm"
              appearance="ghost"
              className="h-7 gap-1.5 text-xs"
              onClick={() => {
                const defaults = buildAgentDefaults(agent);
                behaviorForm.reset(defaults);
                setPromptDraft(agent.systemPrompt);
                setPromptDirty(agent.systemPrompt !== (snap.systemPrompt ?? ''));
              }}
            >
              <RotateCcw className="size-3" />
              Sync from agent
            </Button>
          </div>
        )}
        <Form {...behaviorForm}>
          <FormLayout
            className="sm:pt-4"
            actionsPosition="sticky"
            isDirty={isDraft && configIsDirty}
            cancelAction={{
              label: 'Discard',
              type: 'button',
              onClick: () => {
                behaviorForm.reset(buildBehaviorDefaults(snap));
                setPromptDraft(snap.systemPrompt ?? '');
                setPromptDirty(false);
              },
            }}
            submitAction={{
              label: 'Save changes',
              type: 'button',
              disabled: updateRelease.isPending,
              onClick: () => void behaviorForm.handleSubmit(saveAll)(),
            }}
          >
            <FormSection title="Model" asCard>
              <div>
                <div className="flex items-center justify-between mb-0.5">
                  <div className="flex items-center gap-1">
                    <p className="text-sm font-medium">Required capabilities</p>
                    <CapabilityLegendPopover />
                  </div>
                  {isDraft && (
                    <button
                      type="button"
                      onClick={() =>
                        behaviorForm.setValue(
                          'requiredCapabilities',
                          agent?.guardrails.requiredCapabilities ?? [],
                          { shouldDirty: true },
                        )
                      }
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                      aria-label="Reset capabilities to agent defaults"
                    >
                      <RotateCcw className="size-3" />
                      Reset to agent
                    </button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  {isDraft
                    ? 'Toggle required capabilities. Only compatible models will be shown below.'
                    : 'Capabilities this release requires. Compatible models are shown in the selector.'}
                </p>
                {isDraft ? (
                  <FormField
                    control={behaviorForm.control}
                    name="requiredCapabilities"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <div className="flex flex-wrap gap-1.5">
                            {CAPABILITIES.map((cap) => (
                              <CapabilityToggle
                                key={cap.key}
                                cap={cap}
                                selected={field.value.includes(cap.key)}
                                onToggle={() => {
                                  const current = field.value;
                                  field.onChange(
                                    current.includes(cap.key)
                                      ? current.filter((k: string) => k !== cap.key)
                                      : [...current, cap.key],
                                  );
                                }}
                              />
                            ))}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {CAPABILITIES.map((cap) => (
                      <CapabilityBadge
                        key={cap.key}
                        cap={cap}
                        supported={watchRequiredCaps.includes(cap.key)}
                      />
                    ))}
                  </div>
                )}
              </div>

              <FormField
                control={behaviorForm.control}
                name="modelAlias"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Model</FormLabel>
                    {registry.length > 0 ? (
                      <FormControl>
                        <ModelSelectPopover
                          value={field.value || undefined}
                          onChange={(v) => field.onChange(v ?? '')}
                          models={filteredModels}
                          providers={filteredProviders}
                          disabled={!isDraft}
                        />
                      </FormControl>
                    ) : (
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="e.g. claude-sonnet-4-6"
                          disabled={!isDraft}
                        />
                      </FormControl>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormSection>

            <FormSection title="System Prompt" asCard>
              {!snap.systemPrompt && !isDraft ? (
                <p className="text-sm text-muted-foreground">No system prompt in this snapshot.</p>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm font-medium leading-none">System Prompt</p>
                  <MarkdownInput
                    value={promptDraft}
                    onChange={handlePromptChange}
                    readOnly={!isDraft}
                    placeholder="Write the system prompt…"
                    minHeight="360px"
                    expandLabel="System Prompt"
                  />
                </div>
              )}
            </FormSection>

            <FormSection title="Salutation" asCard>
              <div className="space-y-4">
                <FormField
                  control={behaviorForm.control}
                  name="initialSalutation"
                  render={({ field }) => (
                    <SwitchCard
                      id="initialSalutation"
                      label="Agent greets first"
                      description="The agent initiates the conversation with a salutation instead of waiting for the user's first message."
                      icon={<MessageSquare className="size-5 text-muted-foreground" />}
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={!isDraft}
                    />
                  )}
                />
                <SalutationExamplesField control={behaviorForm.control} disabled={!isDraft} />
              </div>
            </FormSection>

            <FormSection title="Guardrails" asCard columns={3}>
              <FormField
                control={behaviorForm.control}
                name="approvalMode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Approval Mode</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value} disabled={!isDraft}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="NONE">None</SelectItem>
                        <SelectItem value="REQUIRED">Required</SelectItem>
                        <SelectItem value="SUGGESTED">Suggested</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormItem>
                )}
              />
              <FormField
                control={behaviorForm.control}
                name="maxToolCalls"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Max Tool Calls
                      <FieldHint>Maximum tool invocations per run.</FieldHint>
                    </FormLabel>
                    <FormControl>
                      <Input type="number" min={1} {...field} disabled={!isDraft} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={behaviorForm.control}
                name="maxSteps"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Max Steps
                      <FieldHint>Maximum reasoning cycles before forced stop.</FieldHint>
                    </FormLabel>
                    <FormControl>
                      <Input type="number" min={1} {...field} disabled={!isDraft} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </FormSection>

            <FormSection title="Tools" asCard>
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  {(snap.toolRefs ?? []).length} tool
                  {(snap.toolRefs ?? []).length !== 1 ? 's' : ''} enabled
                </p>
                {isDraft && (
                  <Button
                    type="button"
                    size="sm"
                    appearance="outline"
                    onClick={() => setToolPickerOpen(true)}
                  >
                    <SlidersHorizontal className="size-3.5 mr-1.5" />
                    Manage Tools
                  </Button>
                )}
              </div>

              {groupedTools.length === 0 ? (
                <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed py-8 text-center">
                  <div className="flex size-9 items-center justify-center rounded-full bg-muted">
                    <Wrench className="size-4 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground">No tools enabled</p>
                  {isDraft && (
                    <Button
                      type="button"
                      size="sm"
                      appearance="ghost"
                      onClick={() => setToolPickerOpen(true)}
                    >
                      Add tools
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {groupedTools.map(([prefix, items]) => (
                    <div key={prefix}>
                      <p className="text-[11px] font-mono font-medium text-muted-foreground mb-1.5">
                        {prefix}.*
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {items.map((item) => (
                          <Badge
                            key={item.value}
                            variant="secondary"
                            appearance="soft"
                            className="font-mono text-xs gap-1.5 pr-1"
                          >
                            <Wrench className="size-3" />
                            {item.label !== item.value ? item.label : item.value}
                            {isDraft && (
                              <button
                                type="button"
                                disabled={updateRelease.isPending}
                                aria-label={`Remove ${item.value}`}
                                onClick={() =>
                                  void saveToolRefs(
                                    (snap.toolRefs ?? []).filter((r) => r !== item.value),
                                  )
                                }
                                className="ml-0.5 rounded-full opacity-60 hover:opacity-100 disabled:pointer-events-none"
                              >
                                <X className="size-2.5" />
                              </button>
                            )}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <ToolSkillPickerDialog
                open={toolPickerOpen}
                onOpenChange={setToolPickerOpen}
                title="Tools"
                catalog={toolCatalog}
                catalogLoading={toolsLoading}
                selectedRefs={snap.toolRefs ?? []}
                isSaving={updateRelease.isPending}
                onConfirm={async (newRefs) => {
                  await saveToolRefs(newRefs);
                }}
              />
            </FormSection>

            <FormSection title="Skills" asCard>
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  {(snap.skillRefs ?? []).length} skill
                  {(snap.skillRefs ?? []).length !== 1 ? 's' : ''} available
                </p>
                {isDraft && (
                  <Button
                    type="button"
                    size="sm"
                    appearance="outline"
                    onClick={() => setSkillPickerOpen(true)}
                  >
                    <SlidersHorizontal className="size-3.5 mr-1.5" />
                    Manage Skills
                  </Button>
                )}
              </div>

              {groupedSkills.length === 0 ? (
                <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed py-8 text-center">
                  <div className="flex size-9 items-center justify-center rounded-full bg-muted">
                    <BookOpen className="size-4 text-muted-foreground" />
                  </div>
                  <p className="text-sm text-muted-foreground">No skills assigned</p>
                  {isDraft && (
                    <Button
                      type="button"
                      size="sm"
                      appearance="ghost"
                      onClick={() => setSkillPickerOpen(true)}
                    >
                      Add skills
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {groupedSkills.map(([group, items]) => (
                    <div key={group}>
                      <p className="text-[11px] font-mono font-medium text-muted-foreground mb-1.5">
                        {group}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {items.map((item) => (
                          <Badge
                            key={item.value}
                            variant="secondary"
                            appearance="soft"
                            className="font-mono text-xs gap-1.5 pr-1"
                          >
                            <BookOpen className="size-3" />
                            {item.label !== item.value ? item.label : item.value}
                            {isDraft && (
                              <button
                                type="button"
                                disabled={updateRelease.isPending}
                                aria-label={`Remove ${item.value}`}
                                onClick={() =>
                                  void saveSkillRefs(
                                    (snap.skillRefs ?? []).filter((r) => r !== item.value),
                                  )
                                }
                                className="ml-0.5 rounded-full opacity-60 hover:opacity-100 disabled:pointer-events-none"
                              >
                                <X className="size-2.5" />
                              </button>
                            )}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <ToolSkillPickerDialog
                open={skillPickerOpen}
                onOpenChange={setSkillPickerOpen}
                title="Skills"
                catalog={skillCatalog}
                catalogLoading={skillsLoading}
                selectedRefs={snap.skillRefs ?? []}
                isSaving={updateRelease.isPending}
                onConfirm={async (newRefs) => {
                  await saveSkillRefs(newRefs);
                }}
              />
            </FormSection>

            {agentId && (
              <FormSection title="Attachments" asCard>
                <ResourceAttachmentPanel
                  embedded
                  resourceType="agent"
                  resourceId={agentId}
                  tenantSlug={agent?.tenantSlug ?? tenantSlug}
                />
              </FormSection>
            )}
          </FormLayout>
        </Form>
      </div>
    </div>
  );
}

// ─── Export ───────────────────────────────────────────────────────────────────

export function ReleaseDetailPage({
  tenantSlug = '',
  backPath,
}: {
  tenantSlug?: string;
  backPath?: string;
} = {}) {
  return (
    <MobileNavDrillProvider>
      <ReleaseDetailPageInner tenantSlug={tenantSlug} backPath={backPath} />
    </MobileNavDrillProvider>
  );
}
